import React, { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  DeploymentsList,
  FunctionBehaviorChartContainer,
  FunctionsLineChart,
  ReferenceIndicatorPopover,
} from './FunctionBehaviorChart.styles';
import { FunctionData } from '@hud/portal-dtos/webapp-function.dto';
import { GraphsData, Deployments, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import type {
  DataKey,
  DataKeyDef,
  XReferenceLine,
} from '../../Charts/LineChart/LineChart';
import ReferenceIndicator from '../../Charts/ReferenceIndicator/ReferenceIndicator';
import { useElementSize } from '@mantine/hooks';
import {
  formatBigNumbers,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTime12Hour,
  formatTime12HourWithDay,
  isTimeFrameLessThanDay,
  isTimeFrameLessThan2Days,
} from '../../utils';
import { maxBy } from 'lodash';
import { MetricHeader } from './MetricHeader';
import Metric from '../../Metric/Metric';
import { FilterMenuButton } from './FilterMenuButton';
import { P90_TOOLTIP, P90_TOOLTIP_URL } from '../P90InfoHover/P90InfoHover';

export type Range = {
  start: Date;
  end: Date;
};

export interface FunctionBehaviorChartProps {
  function?: FunctionData;
  graph?: GraphsData;
  deployments?: Deployments;
  loading: boolean;
  compact?: boolean;
  onRangeSelected?: (range: Range) => void;
  timeFrame: TimeFrame;
  firstSeenAt?: Date;
}

const SINGLE_THRESHOLD_INTERVAL = 2;

const roundToBeginningOfHour = (date: Date): Date => {
  const newDate = new Date(date.getTime()); // Clone the original date
  newDate.setMinutes(0, 0, 0); // Sets minutes, seconds, and milliseconds to zero
  return newDate;
};

export default function FunctionBehaviorChart({
  function: func,
  graph,
  deployments,
  loading,
  compact = false,
  onRangeSelected,
  timeFrame,
  firstSeenAt,
}: FunctionBehaviorChartProps) {
  const theme = useTheme();
  const { ref, width } = useElementSize();
  const [hiddenDurationKeys, setHiddenDurationKeys] = useState<string[]>([]);

  const maxInvocations = maxBy(graph, 'runs')?.runs || 0;
  const maxDuration = Math.max(
    maxBy(graph, 'duration')?.duration || 0,
    maxBy(graph, 'durationP90')?.durationP90 || 0,
  );
  const maxErrorRate = maxBy(graph, 'errorRate')?.errorRate || 0;

  const isMaxInvocationsThreshold = maxInvocations < 0.01;
  const isMaxDurationThreshold = maxDuration < 0.00000001;
  const isMaxErrorRateThreshold = maxErrorRate < 0.01;

  const normalizedGraph: GraphsData = useMemo(() => {
    const normalized =
      graph?.map((g) => {
        const funcFirstSeenAt = func?.firstSeenAt
          ? roundToBeginningOfHour(func.firstSeenAt)
          : null;
        if (funcFirstSeenAt && g.timestamp < funcFirstSeenAt && !g.runs) {
          return {
            timestamp: g.timestamp,
            runs: null,
            duration: null,
            durationP90: null,
            errorRate: null,
          };
        }
        return {
          timestamp: g.timestamp,
          runs: isMaxInvocationsThreshold
            ? g.runs === 0 || g.runs === null
              ? g.runs
              : maxInvocations
            : g.runs,
          duration: isMaxDurationThreshold
            ? g.duration === 0 || g.duration === null
              ? g.duration
              : maxDuration
            : g.duration,
          durationP90: isMaxDurationThreshold
            ? g.durationP90 === 0 || g.durationP90 === null || g.durationP90 === undefined
              ? g.durationP90
              : maxDuration
            : g.durationP90,
          errorRate: isMaxErrorRateThreshold
            ? g.errorRate === 0 || g.errorRate === null
              ? g.errorRate
              : maxErrorRate
            : Math.min(1, g.errorRate ?? 0), // cap error rate at 1
        };
      }) ?? [];
    return normalized;
  }, [
    func,
    graph,
    isMaxDurationThreshold,
    isMaxErrorRateThreshold,
    isMaxInvocationsThreshold,
    maxDuration,
    maxErrorRate,
    maxInvocations,
  ]);

  const references = useMemo(() => {
    if (!graph || !deployments || graph.length < 2) return [];

    // Calculate the interval between the first two graph points
    const interval = graph[1].timestamp.getTime() - graph[0].timestamp.getTime();

    return graph
      .map((g) => {
        const relevantDeployments = deployments.filter((deployment) => {
          const deploymentTime = deployment.timestamp.getTime();
          const graphTime = g.timestamp.getTime();
          return deploymentTime >= graphTime && deploymentTime < graphTime + interval;
        });

        return {
          xAxisValue: g.timestamp,
          deployments: relevantDeployments,
        };
      })
      .filter((reference) => reference.deployments.length > 0); // Filter out empty references
  }, [deployments, graph]);

  const renderReferenceLine = useCallback((value: XReferenceLine) => {
    const deployments = value.deployments as Deployments;
    return (
      <ReferenceIndicatorPopover
        body={
          deployments.length === 1 ? (
            <div>Deployed at {formatDateTime(deployments[0].timestamp)}</div>
          ) : (
            <div>
              <div>{deployments.length} Deployments</div>
              <div>
                {formatDateTime(deployments[0].timestamp)} -{' '}
                {formatDateTime(deployments[deployments.length - 1].timestamp)}
              </div>
            </div>
          )
        }
        clickBody={
          deployments.length > 1 ? (
            <DeploymentsList>
              {deployments.map((deployment, index) => (
                <div key={index}>{formatDateTime(deployment.timestamp)}</div>
              ))}
            </DeploymentsList>
          ) : null
        }
      >
        <ReferenceIndicator count={deployments.length} />
      </ReferenceIndicatorPopover>
    );
  }, []);

  function toggleHiddenDurationKey(key: string) {
    if (hiddenDurationKeys.includes(key)) {
      setHiddenDurationKeys(hiddenDurationKeys.filter((k) => k !== key));
    } else {
      setHiddenDurationKeys([...hiddenDurationKeys, key]);
    }
  }

  const durationDataKeys: DataKey[] = [
    {
      key: 'duration',
      label: 'Avg Dur',
      hidden: hiddenDurationKeys.includes('duration'),
    },
    {
      key: 'durationP90',
      color: theme.purpleHome,
      label: 'P90 Dur',
      hidden: hiddenDurationKeys.includes('durationP90'),
    },
  ];

  const dataKeyDefs: DataKeyDef[] = [
    {
      title: 'Invocations',
      yInterval: isMaxInvocationsThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatBigNumbers(value),
      tooltipFormatter: (value: number) => formatNumber(value),
      dataKeys: [{ key: 'runs' }],
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Total:',
              metric: <Metric metric={func?.metrics.runs} unit="abbreviated" />,
            },
            ...(!isTimeFrameLessThanDay(timeFrame, firstSeenAt)
              ? [{
                  title: 'Runs/Day:',
                  metric: (
                    <Metric
                      metric={func?.metrics.normalizedRuns}
                      showTrend
                      trendTooltip="The trend change in how many times the function is invoked per day - last 24h vs last 7d"
                      unit="abbreviated"
                    />
                  ),
                }]
              : []),
          ]}
        />
      ),
    },
    {
      title: (
        <>
          Duration
          <FilterMenuButton
            dataKeys={durationDataKeys}
            hiddenKeys={hiddenDurationKeys}
            onToggle={toggleHiddenDurationKey}
          />
        </>
      ),
      dataKeys: durationDataKeys,
      yInterval: isMaxDurationThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatDuration(value, 0),
      tooltipFormatter: (value: number) => formatDuration(value, 0),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Avg:',
              metric: (
                <Metric
                  metric={func?.metrics.durationAvg}
                  showTrend
                  unit="duration"
                  trendTooltip="The trend change in the average duration of the function - last 24h vs last 7d"
                />
              ),
              color: theme.turquoise,
              hidden: hiddenDurationKeys.includes('duration'),
            },
            {
              title: 'P90:',
              tooltip: P90_TOOLTIP,
              tooltipUrl: P90_TOOLTIP_URL,
              metric: (
                <Metric
                  metric={func?.metrics.durationP90}
                  showTrend
                  unit="duration"
                  trendTooltip="The trend change in the P90 duration of the function - last 24h vs last 7d"
                />
              ),
              color: theme.purpleHome,
              hidden: hiddenDurationKeys.includes('durationP90'),
            },
          ]}
        />
      ),
    },
    {
      title: 'Error Rate',
      domain: [0, maxErrorRate === 0 ? 1 : 'auto'],
      dataKeys: [{ key: 'errorRate' }],
      labelFormatter: (value: number) => formatPercentage(value),
      tooltipFormatter: (value: number) => formatPercentage(value),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Rate:',
              metric: (
                <Metric
                  metric={func?.metrics.errorRate}
                  showTrend
                  unit="percent"
                  trendTooltip="The trend change in the percentage of function invocations returning 5xx errors - last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
  ];

  const isLessThan2Days = isTimeFrameLessThan2Days(timeFrame, firstSeenAt);

  return (
    <FunctionBehaviorChartContainer ref={ref}>
      <FunctionsLineChart
        compact={compact}
        graphId="functionBehaviorChart"
        width={width}
        headerWidth={210}
        height={100}
        interval={isLessThan2Days ? undefined : 23}
        data={normalizedGraph}
        labelFormatter={(value, index) => {
          if (isLessThan2Days) {
            if (index === 0) {
              return formatTime12HourWithDay(new Date(value));
            }
            return formatTime12Hour(new Date(value));
          }
          return formatDateShort(new Date(value));
        }}
        renderReference={renderReferenceLine}
        dataKeyDefs={dataKeyDefs}
        XdataKey="timestamp"
        referenceLines={references}
        onRangeSelected={onRangeSelected}
      />
    </FunctionBehaviorChartContainer>
  );
}
